import React, { useEffect, useState } from "react";
import { Image } from "antd";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import useModalToggle from "../../../custom_hooks/useModalToggle";
import ConfirmModal from "../../../components/ConfirmModal";
import svg29 from "../../../assets/svg/svg29.svg";
import svg46 from "../../../assets/svg/svg46.svg";
import { save } from "../../../features/save/saveSlice";
import { formatImgPath } from "../../../utils";
import ProgressImageAdd from "./ProgressImageAdd";

const MIN_IMAGES = 4;
const MAX_IMAGES = 10;

function Images({ form, existingImages, fieldName = "projImages" }) {
  const dispatch = useDispatch();

  const parseImages = (images) => {
    if (!images) return [];
    if (Array.isArray(images)) return images;
    return [];
  };

  const [images, setImages] = useState(() => parseImages(existingImages));

  useEffect(() => {
    const parsed = parseImages(existingImages);
    if (parsed.length > 0) {
      setImages(parsed);
      form.setFieldsValue({ [fieldName]: parsed });
    }
  }, [existingImages]);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const { open, handleCancel, handleOpen } = useModalToggle();

  const notify = (updated) => {
    form.setFieldsValue({ [fieldName]: updated });
  };

  const handleAdd = (image) => {
    const updated = [...images, image];
    setImages(updated);
    notify(updated);
  };

  const handleDelete = async () => {
    if (deleteTarget?.attId) {
      const res = await dispatch(
        save({ url: "attachment/delete_attachment", attId: deleteTarget.attId })
      );
      if (!res?.payload?.success) {
        toast.error(res?.payload?.message || "Failed to delete image");
        return;
      }
    }
    const updated = images.filter((img) => img !== deleteTarget);
    setImages(updated);
    notify(updated);
    setDeleteTarget(null);
  };

  const atMax = images.length >= MAX_IMAGES;

  return (
    <div className="mt-[1.25rem]">
      <div className="flex items-center justify-between py-[0.8125rem] px-[1.25rem] bg-[#F1F3F5] rounded-[.5rem]">
        <div className="flex items-center gap-x-[1.25rem]">
          <span className="text-[#23262F] text-[1rem] font-[600] font-interSans">
            Uploaded Images
          </span>
          <span
            className={`text-sm font-medium ${
              images.length < MIN_IMAGES ? "text-red-500" : "text-green-600"
            }`}
          >
            {images.length} / {MAX_IMAGES} uploaded
            {images.length < MIN_IMAGES && ` (at least ${MIN_IMAGES} required)`}
          </span>
        </div>
        {!atMax && (
          <button
            onClick={handleOpen}
            type="button"
            className="bg-[#0092CC] flex items-center justify-center p-[.5rem] rounded-[.375rem]"
          >
            <img src={svg29} alt="add" />
          </button>
        )}
      </div>

      <div className="grid grid-cols-4 gap-3 mt-5">
        {images.map((item, index) => (
          <div key={index} className="relative border rounded overflow-hidden aspect-square">
            <Image
              loading="lazy"
              wrapperClassName="!block !w-full !h-full"
              className="!w-full !h-full object-cover"
              src={formatImgPath(item?.attFile)}
              alt={item?.attDesc || `image-${index}`}
              preview={{ mask: item?.attDesc || "Preview" }}
            />
            <div className="absolute top-1 left-1 right-1 flex justify-between items-start">
              <span className="bg-black/50 text-white text-xs px-1 rounded max-w-[70%] truncate">
                {item?.attDesc}
              </span>
              <button
                type="button"
                onClick={() => setDeleteTarget(item)}
                className="bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs flex-shrink-0"
              >
                ✕
              </button>
            </div>
          </div>
        ))}
      </div>

      <ProgressImageAdd
        isModalOpen={open}
        handleCancel={handleCancel}
        onAdd={handleAdd}
      />

      <ConfirmModal
        open={!!deleteTarget}
        handleCancel={() => setDeleteTarget(null)}
        handleSubmit={handleDelete}
        content="Are you sure you want to delete this image?"
        type="alert"
        btnTitle="Delete"
      />
    </div>
  );
}

export default Images;
